import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const HeroSection = ({ onOrderNow }) => {
  const navigate = useNavigate();

  const handleReservation = () => {
    navigate('/table-reservation');
  };

  const handleTrackShipment = () => {
    navigate('/order-tracking');
  };

  // Floating animation variants for shipping items
  const floatingVariants = {
    float1: {
      y: [0, -20, 0],
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    float2: {
      y: [0, -15, 0],
      rotate: [0, -3, 3, 0],
      transition: {
        duration: 3.5,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 0.5
      }
    },
    float3: {
      y: [0, -25, 0],
      rotate: [0, 8, -8, 0],
      transition: {
        duration: 4.5,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 1
      }
    },
    float4: {
      y: [0, -18, 0],
      rotate: [0, -6, 6, 0],
      transition: {
        duration: 3.8,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 1.5
      }
    },
    float5: {
      y: [0, -22, 0],
      rotate: [0, 4, -4, 0],
      transition: {
        duration: 4.2,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2
      }
    },
    float6: {
      y: [0, -16, 0],
      rotate: [0, -7, 7, 0],
      transition: {
        duration: 3.6,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 0.8
      }
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <Image
          src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55"
          alt="Modern logistics warehouse with organized shipping containers and freight trucks for international delivery"
          className="w-full h-full object-cover"
        />
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-black/50"></div>
      </div>
      {/* Floating Shipping Items */}
      <div className="absolute inset-0 z-5 pointer-events-none">
        {/* Package - Top Left */}
        <motion.div
          className="absolute top-20 left-8 text-6xl opacity-80"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.8, scale: 1, ...floatingVariants?.float1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          📦
        </motion.div>

        {/* Truck - Top Right */}
        <motion.div
          className="absolute top-32 right-12 text-5xl opacity-75"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.75, scale: 1, ...floatingVariants?.float2 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          🚚
        </motion.div>

        {/* Airplane - Middle Left */}
        <motion.div
          className="absolute top-1/2 left-16 text-4xl opacity-70"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.7, scale: 1, ...floatingVariants?.float3 }}
          transition={{ delay: 1.1, duration: 0.6 }}
        >
          ✈️
        </motion.div>

        {/* Ship - Middle Right */}
        <motion.div
          className="absolute top-1/3 right-20 text-5xl opacity-75"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.75, scale: 1, ...floatingVariants?.float4 }}
          transition={{ delay: 1.4, duration: 0.6 }}
        >
          🚢
        </motion.div>

        {/* Globe - Bottom Left */}
        <motion.div
          className="absolute bottom-40 left-12 text-4xl opacity-65"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.65, scale: 1, ...floatingVariants?.float5 }}
          transition={{ delay: 1.7, duration: 0.6 }}
        >
          🌍
        </motion.div>

        {/* Delivery Box - Bottom Right */}
        <motion.div
          className="absolute bottom-52 right-16 text-5xl opacity-70"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.7, scale: 1, ...floatingVariants?.float6 }}
          transition={{ delay: 2, duration: 0.6 }}
        >
          📮
        </motion.div>

        {/* Additional smaller floating items for mobile responsiveness */}
        <motion.div
          className="absolute top-1/4 left-1/4 text-3xl opacity-50 hidden sm:block"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.5, scale: 1, ...floatingVariants?.float1 }}
          transition={{ delay: 2.3, duration: 0.6 }}
        >
          🚛
        </motion.div>

        <motion.div
          className="absolute bottom-1/3 right-1/3 text-3xl opacity-55 hidden sm:block"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.55, scale: 1, ...floatingVariants?.float2 }}
          transition={{ delay: 2.6, duration: 0.6 }}
        >
          📡
        </motion.div>
      </div>
      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center text-center" style={{ minHeight: 'calc(100vh - 8rem)', paddingTop: '4rem', paddingBottom: '8rem' }}>
          
          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-5xl sm:text-6xl lg:text-8xl font-heading font-bold text-white leading-tight mb-8 drop-shadow-2xl"
          >
            Global Shipping.
            <br />
            <span className="text-blue-400">Delivered with Care.</span>
          </motion.h1>

          {/* Attractive Offer Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12 max-w-4xl mx-auto"
          >
            <p className="text-2xl sm:text-3xl lg:text-4xl text-white font-semibold mb-4 drop-shadow-lg">
              🌐 International Logistics Made Simple! 🌐
            </p>
            <p className="text-xl sm:text-2xl text-blue-100 font-medium mb-6 drop-shadow-md">
              Get <span className="text-blue-400 font-bold text-2xl sm:text-3xl">Real-Time Tracking</span> on all shipments + 
              <span className="text-white font-bold"> 24/7 Support</span>
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-lg sm:text-xl text-white">
              <span className="bg-blue-500/80 px-4 py-2 rounded-full backdrop-blur-sm">
                ⚡ Express delivery worldwide
              </span>
              <span className="bg-blue-500/80 px-4 py-2 rounded-full backdrop-blur-sm">
                🛡️ Secure & insured shipping
              </span>
            </div>
            <p className="text-lg text-blue-200 mt-4 font-medium">
              *Track your shipment in real-time from pickup to delivery
            </p>
          </motion.div>

          {/* Call to Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-6 justify-center"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={handleReservation}
              className="text-xl px-10 py-5 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-lg shadow-2xl hover:scale-105 transition-all duration-300 border-0"
            >
              Book Shipment
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              onClick={handleTrackShipment}
              className="text-xl px-10 py-5 border-2 border-white text-white hover:bg-white hover:text-black font-bold rounded-lg shadow-2xl hover:scale-105 transition-all duration-300"
            >
              Track Shipment
            </Button>
          </motion.div>

        </div>
      </div>
      {/* Paintbrush Transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 overflow-hidden z-10">
        <svg
          viewBox="0 0 1200 120"
          className="w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0,60 C300,120 600,0 900,60 C1050,90 1150,30 1200,60 L1200,120 L0,120 Z"
            fill="var(--color-background)"
            className="drop-shadow-lg"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;